import { camelCase, isEmpty, kebabCase } from 'lodash';
import { string } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, HeroBanner, Icon, SanitizedHtml, Skeleton } from 'components';
import { useFetch } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

/**
 * @description Display content of legal pages which are stored in Strapi
 * @param {type} type of legal page corresponding to page_type in Strapi : terms | terms-of-sales | insurance
 */
const LegalPage = ({ type }) => {
  const { t } = useTranslation();
  const { call: legalPageCall, data: legalContent, loading: legalLoading } = useFetch();

  const breadcrumbItems = useMemo(() => {
    return [
      { url: routes.home.path, label: t('breadcrumb.home') },
      { url: routes[camelCase(type)].path, label: legalContent?.data?.[0]?.title }
    ];
  }, [type, legalContent]);

  useEffect(() => {
    getLegalPage();
  }, [type]);

  const handleDownloadPDF = () => {
    window.print();
  };

  const getLegalPage = () => {
    legalPageCall({
      service: appService.getStrapiServiceName(),
      path: '/api/legal-pages',
      method: 'get',
      query: {
        filters: {
          page_type: {
            name: {
              $eq: type
            }
          }
        }
      }
    });
  };

  return (
    <>
      <HeroBanner
        title={legalContent?.data?.[0]?.title ?? ''}
        image={legalContent?.data?.[0]?.heroImageDesktop}
        mobileImage={legalContent?.data?.[0]?.heroImageMobile}
        loading={legalLoading}
        className="print:hidden"
      />

      <img
        className="hidden w-auto h-14 mx-auto my-6 print:block"
        src={appService.loadBrandImage('logo')}
        alt={t('images.logoAlt')}
        width="154"
        height="56"
      />

      <p className="h1 m-4 text-center hidden print:block">{legalContent?.data?.[0]?.title}</p>

      {legalLoading && (
        <div className="grid grid-cols-12 gap-3 main-container">
          <div className="col-span-4 hidden md:block">
            {[...Array(10)].map((_, index) => (
              <Skeleton type="text" classNames="h-4 w-full mb-3" key={`menu-skeleton-${index}`} />
            ))}
          </div>

          <div className="col-span-12 lg:col-span-8">
            {[...Array(10)].map((_, index) => (
              <div key={`legal-skeleton-${index}`}>
                <Skeleton type="text" size="lg" classNames="w-full mb-3" />
                <Skeleton type="text" classNames="w-[75%] mb-3" />
              </div>
            ))}
          </div>
        </div>
      )}

      {!legalLoading && !isEmpty(legalContent?.data?.[0]) && (
        <div className="grid grid-cols-12 gap-3 main-container lg:mt-20">
          <div className="hidden col-span-12 md:block">
            <Breadcrumb breadcrumbItems={breadcrumbItems} />
          </div>

          <div className="col-span-4 hidden md:block">
            <div className="md:sticky md:top-4">
              <ul>
                {legalContent.data[0].paragraphs.map((paragraph) => (
                  <li key={`paragraph-${paragraph.id}`} className="mt-2 flex items-center">
                    <span className="nav-item mr-2" />
                    <a href={`#${kebabCase(paragraph.title)}`} className="h3">
                      {paragraph.title}
                    </a>
                  </li>
                ))}
              </ul>

              {type === 'terms-of-sales' && (
                <button type="button" className="uppercase btn small w-fit mt-2" onClick={handleDownloadPDF}>
                  <Icon name="download" libraryIconProps={{ size: 'lg' }} classes="mr-2 md:hidden lg:block" />
                  {t('common:termsOfSales.downloadPdf')}
                </button>
              )}
            </div>
          </div>

          <div className="col-span-12 md:col-span-8">
            <ul className="mt-12 md:mt-0">
              {legalContent.data[0].paragraphs.map((paragraph, index) => (
                <li key={`paragraph-${kebabCase(paragraph.title)}-${index}`} id={kebabCase(paragraph.title)}>
                  <h2 className="h2 underlined-blue mb-4 print:mt-6">{paragraph.title}</h2>
                  <SanitizedHtml content={paragraph?.content} className="mb-4 print:mb-6" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

LegalPage.propTypes = {
  type: string.isRequired
};

export default LegalPage;
